import {
  AdminDatePicker,
  Alert,
  App,
  CreateButton,
  Form,
  Input,
  InputNumber,
  Modal,
  Typography,
} from '@wanjianshi/admin-ui';
import type { ApiResponse } from '@wanjianshi/shared-contracts';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useState } from 'react';
import { AdminRemoteSelect } from '@/components/AdminRemoteSelect';
import {
  adminApi,
  adminOperationOptions,
  createIdempotencyKey,
  isAdminUnauthorizedError,
} from '@/services/api/client';

interface RechargeUserOption {
  id: string;
  nickname?: string | null;
  mobile?: string | null;
  account_points?: string | number | null;
}

interface ManualRechargeFormValues {
  user_id: string;
  pay_amount_yuan: number;
  credited_points: number;
  bank_transaction_no: string;
  received_date: Dayjs;
  reason: string;
}

function userOptionLabel(user: RechargeUserOption): string {
  const nickname = user.nickname?.trim() || `用户 ${user.id}`;
  const mobile = user.mobile?.trim() || '未绑定手机号';
  const points = new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 6 }).format(
    Number(user.account_points || 0),
  );
  return `${mobile} · ${nickname} · 当前 ${points} 积分`;
}

export function ManualRechargeModal({ onSaved }: { onSaved: () => Promise<void> }) {
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pointsManuallyEdited, setPointsManuallyEdited] = useState(false);
  const [form] = Form.useForm<ManualRechargeFormValues>();

  const loadUsers = useCallback(async (keyword: string, signal: AbortSignal) => {
    const response = await adminApi.get<ApiResponse<{ items: RechargeUserOption[] }>>(
      '/admin/recharge/user-options',
      { params: { keyword, limit: 20 }, signal },
    );
    return response.data.items ?? [];
  }, []);

  const openModal = () => {
    setOpen(true);
    setPointsManuallyEdited(false);
    form.resetFields();
    form.setFieldsValue({
      received_date: dayjs(),
      pay_amount_yuan: undefined,
      credited_points: undefined,
    });
  };

  const closeModal = () => {
    if (submitting) return;
    setOpen(false);
    form.resetFields();
  };

  const submit = async () => {
    const values = await form.validateFields();
    const reason = values.reason.trim();
    const idempotencyKey = createIdempotencyKey('manual-recharge');
    setSubmitting(true);
    try {
      await adminApi.post(
        '/admin/recharge/manual-paid',
        {
          user_id: Number(values.user_id),
          pay_amount_yuan: values.pay_amount_yuan,
          credited_points: values.credited_points,
          bank_transaction_no: values.bank_transaction_no.trim(),
          received_date: values.received_date.format('YYYY-MM-DD'),
          reason,
          idempotency_key: idempotencyKey,
        },
        adminOperationOptions(reason, idempotencyKey),
      );
      message.success('公对公充值已入账，订单和积分流水已生成');
      setOpen(false);
      form.resetFields();
      await onSaved();
    } catch (error) {
      if (!isAdminUnauthorizedError(error)) {
        message.error(error instanceof Error ? error.message : '手工充值入账失败');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <CreateButton onClick={openModal}>手工充值</CreateButton>
      <Modal
        open={open}
        width={760}
        title="公对公手工充值"
        okText="确认到账并发放积分"
        cancelText="取消"
        confirmLoading={submitting}
        maskClosable={!submitting}
        onCancel={closeModal}
        onOk={() => void submit()}
        className="manual-recharge-modal"
      >
        <Alert
          showIcon
          type="info"
          title="仅用于已经确认到账的公对公转账"
          description="提交后会立即生成支付成功订单、充值记录和积分流水。银行流水号只能使用一次，重复提交不会再次发放积分。"
        />

        <Form<ManualRechargeFormValues>
          form={form}
          layout="vertical"
          requiredMark="optional"
          className="manual-recharge-form"
        >
          <Form.Item
            name="user_id"
            label="到账账户"
            rules={[{ required: true, message: '请选择到账账户' }]}
          >
            <AdminRemoteSelect<RechargeUserOption>
              allowClear
              loadOptions={loadUsers}
              mapOption={user => ({
                value: String(user.id),
                label: userOptionLabel(user),
              })}
              placeholder="输入用户昵称或手机号搜索"
            />
          </Form.Item>

          <div className="manual-recharge-form__grid">
            <Form.Item
              name="pay_amount_yuan"
              label="实际到账金额"
              extra="单位：元"
              rules={[{ required: true, message: '请输入实际到账金额' }]}
            >
              <InputNumber
                min={0.01}
                max={9_999_999_999.99}
                precision={2}
                step={100}
                style={{ width: '100%' }}
                placeholder="例如 1000.00"
                addonAfter="元"
                onChange={value => {
                  if (!pointsManuallyEdited && typeof value === 'number') {
                    form.setFieldValue('credited_points', Math.round(value * 100));
                  }
                }}
              />
            </Form.Item>

            <Form.Item
              name="credited_points"
              label="到账积分"
              extra="默认按 1 元 = 100 积分，可按实际约定修改"
              rules={[{ required: true, message: '请输入到账积分' }]}
            >
              <InputNumber
                min={1}
                max={2_147_483_647}
                precision={0}
                style={{ width: '100%' }}
                placeholder="输入需要发放的积分"
                addonAfter="积分"
                onChange={() => setPointsManuallyEdited(true)}
              />
            </Form.Item>
          </div>

          <div className="manual-recharge-form__grid">
            <Form.Item
              name="bank_transaction_no"
              label="银行流水号 / 回单号"
              rules={[
                { required: true, whitespace: true, message: '请输入银行流水号或回单号' },
                { max: 64, message: '最多输入 64 个字符' },
              ]}
            >
              <Input allowClear maxLength={64} placeholder="用于对账和防止重复入账" />
            </Form.Item>

            <Form.Item
              name="received_date"
              label="到账日期"
              rules={[{ required: true, message: '请选择到账日期' }]}
            >
              <AdminDatePicker
                style={{ width: '100%' }}
                disabledDate={current => current.isAfter(dayjs(), 'day')}
              />
            </Form.Item>
          </div>

          <Form.Item
            name="reason"
            label="入账说明"
            extra={<Typography.Text type="secondary">该说明会同时进入业务记录和后台操作审计。</Typography.Text>}
            rules={[
              { required: true, whitespace: true, message: '请输入入账说明' },
              { max: 200, message: '最多输入 200 个字符' },
            ]}
          >
            <Input.TextArea
              showCount
              maxLength={200}
              autoSize={{ minRows: 3, maxRows: 5 }}
              placeholder="例如：2026 年 8 月企业客户线下采购积分，财务已核对回单"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
